import { getAndParseLocationCookie } from "lib/nekoCookies";
import { CheckInParams } from "lib/types/checkInParameters";
import { useCallback } from "react";
import {
  SeverityLevel,
  useAppInsights,
} from "render/context/AppInsightsContext";
import { useConfig } from "render/context/ConfigContext";

const SITE_ID_MORBY_CENTRUM = "SE-BMA11";

export function useCardioSecurDeeplink({
  patientId,
  visitId,
}: CheckInParams): () => void {
  const config = useConfig();
  const environment = config.stageType;
  const location = getAndParseLocationCookie();
  const appInsights = useAppInsights();
  const siteId = location?.siteId;

  const callback = useCallback(() => {
    const openCardioSecurApp = () => {
      const cardioSecurUrl = createCardioSecurUrl(patientId, visitId);
      window.location.href = cardioSecurUrl.href;
    };

    const openCardioFakeurPageInDevAdminUI = () => {
      const url = `https://admin.dev.cur8.co/patient/${patientId}/visit/${visitId}/cardio-secur`;
      window.open(url, "_blank");
    };

    const logCardioSecurDeeplink = () => {
      appInsights.trackTrace({
        message: "Open CardioSecur app",
        severityLevel: SeverityLevel.Information,
        properties: {
          cardioSecurUrl: createCardioSecurUrl(patientId, visitId).href,
          patientId,
          visitId,
          siteId,
        },
      });
    };

    // In development, Mörby Centrum is a special site where we want to test the real CardioSecur app.
    // Elsewhere, we open a fake CardioSecur page in the Dev Admin UI for testing purposes.
    if (environment === "development" && siteId !== SITE_ID_MORBY_CENTRUM) {
      openCardioFakeurPageInDevAdminUI();
    } else {
      logCardioSecurDeeplink();
      openCardioSecurApp();
    }
  }, [environment, siteId, patientId, visitId, appInsights]);

  return callback;
}

function createCardioSecurUrl(patientId: string, visitId: string) {
  const baseURL = "cardiosecur-pro-requestcreateupdatepatient:///default";

  const queryParams = new URLSearchParams();
  queryParams.append("patient_mgmnt_id", patientId);
  queryParams.append("visit_id", visitId);
  queryParams.append("deferreq", "YES");

  return new URL(`?${queryParams.toString()}`, baseURL);
}
